import type { TokenRequest } from "@sarva/api-client";
import { Button, Field, Icon, Input } from "@sarva/ui";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Link, Navigate, useLocation } from "react-router-dom";
import { ErrorCode } from "@sarva/domain-types";
import { describeProblem, fieldErrorMap, useSessionState } from "@/shared/api";
import { Brand } from "@/shared/layout/Brand";
import { embedRequested } from "@/shared/embed";
import { ProblemNotice } from "@/shared/ProblemNotice";
import { useSignInOrganisation } from "../hooks/useSignInOrganisation";
import { FORGOT_PASSWORD_ROUTE } from "./PasswordRecoveryPages";
import { useSignIn } from "../hooks/useSignIn";
import { coreSignInAvailable, coreSignInUrl } from "../api/core";
import { DEFAULT_SIGNED_IN_ROUTE } from "./RequireAuth";

/**
 * ⚠️  This screen drives `POST /api/v1/auth/token`, the **development** sign-in.
 * Real environments use an OIDC authorization-code flow with PKCE and enforced
 * MFA (doc 23 §2); when that lands, this file and `../api/dev-token.ts` are
 * deleted together. See the header comment on `exchangeDevToken`.
 *
 * ── It asks for a password now, and the password is real ────────────────────
 *
 * This form used to take a tenant UUID and an email address and verify neither
 * — the endpoint issued a token for whoever was named. That is defensible for a
 * test fixture and indefensible on a screen a practice is shown: a login with
 * no password reads as a product nobody has finished.
 *
 * Migration 0050 gave `users` a `password_hash`, `sarva_platform.passwords`
 * hashes with stdlib scrypt, and the router verifies it — so the credential on
 * this form is checked rather than decorative. A password box that accepts
 * anything would have been worse than none: it is the "a flag the caller sets
 * is not a control" defect ([D68]) wearing a security affordance, and it
 * misleads the audience it was added for.
 *
 * ── Where the tenant went ───────────────────────────────────────────────────
 *
 * `VITE_TENANT_ID`, read through `../api/tenant`. Why it is configuration and
 * not something a person types is argued there, in one place, because session
 * restore has to name the same tenant this screen signs in to. The field still
 * appears when the variable is unset, so a developer working against several
 * tenants is not locked out of the one they want.
 *
 * The note at the foot of the form says what this build is, because a sign-in
 * that stands in for OIDC and does not say so is indistinguishable from the
 * real thing. It sits *below* the form rather than above it: it is a caveat
 * about this build, not an instruction the person signing in has to act on.
 *
 * ── Why two panels ──────────────────────────────────────────────────────────
 *
 * doc 40 §12 permits a gradient on exactly three surfaces — marketing, sign-in
 * and empty states — and forbids one behind a form, or any surface showing
 * money or PHI. So the gradient carries the brand panel and the form sits on a
 * plain `bg-default` beside it. The panel is `aria-hidden`: it repeats nothing
 * a screen-reader user needs, and the form is the whole task.
 *
 * The brand panel is a flat `bg-primary-weak` tint with one soft `gradient-weak` wash, kept
 * away from the text (see the note on the panel). No text on it is white or reduced-opacity,
 * so contrast is the ordinary text roles on a near-white ground. (A full-bleed hero gradient
 * was tried in v3 and reverted at Divyansh's request — "the login page was better earlier".)
 */

interface SignInFormValues {
  tenant_id: string;
  email: string;
  password: string;
  /**
   * The second factor, asked for only once the API says it is needed.
   *
   * (H) Not shown up front. An MFA box on a form most people will never need one for teaches
   * them to ignore a field, and the API is the only thing that knows whether this account is
   * enrolled — asking it is a round trip we are making anyway.
   */
  mfa_code: string;
}

interface LocationState {
  from?: { pathname?: string; search?: string };
}

/**
 * The three things the product is, led by the one it is named for.
 *
 * (H) This panel said nothing about AI. It opened "The revenue cycle, run by
 * deadline" and listed three deterministic capabilities — all true, and a
 * strange thing for an *AI-native* practice management system to lead with.
 * The deadline thesis is still here; it is the second point, where it belongs,
 * because "nothing is lost to a clock" is a promise the AI does not make.
 *
 * (H) Every line is a capability that exists. "The model proposes, your team
 * decides" is the product's actual rule, not a reassurance written for a
 * marketing panel — and the patient line is the one AI feature whose output a
 * patient reads, which is why it earns its place over something vaguer.
 */
const PROOF_POINTS = [
  {
    icon: "sparkles" as const,
    tone: "primary" as const,
    title: "AI-powered workflows",
    body: "Denials classified, clinical notes coded, post sorted. The model proposes; your team decides.",
  },
  {
    icon: "clock" as const,
    tone: "danger" as const,
    title: "Nothing lost to a clock",
    body: "Every filing, appeal and authorisation deadline tracked to the day it expires.",
  },
  {
    icon: "chart" as const,
    tone: "success" as const,
    title: "Real-time insight",
    body: "Where the money is, what is at risk, and what needs a person — without building a report.",
  },
] as const;

/**
 * Tile colours for the three points.
 *
 * (H) The tile surface is WHITE, and the tone lives in the glyph.
 *
 * These were `bg-primary-weak` / `bg-danger-weak` / `bg-success-weak` — the
 * weak surfaces the rest of the product uses for tinted blocks. On this panel
 * they disappeared: the panel is itself `bg-primary-weak`, so the first tile was
 * rendering the *exact* same colour as the thing behind it, and the other two
 * were within a few points of it. A weak surface only reads as a surface on a
 * white ground.
 */
const TILE: Record<string, string> = {
  primary: "text-primary",
  danger: "text-danger",
  success: "text-success",
};

interface LocationState {
  from?: { pathname?: string; search?: string };
}

export function SignInPage() {
  const { credentials, signedOutReason } = useSessionState();
  const location = useLocation();
  const signIn = useSignIn();
  const [failure, setFailure] = useState<unknown>(null);
  /**
   * (H) Set by the API's answer, never guessed from the email. Whether an account carries a
   * second factor is a fact about the row, and a client that tried to predict it would be
   * telling somebody which accounts are protected before they have proved anything.
   */
  const [mfaRequired, setMfaRequired] = useState(false);
  const [revealed, setRevealed] = useState(false);
  /** Backend D202 — the organisation this sign-in is for, shown before the password. */
  // `?org=<code>` on a sign-in link a practice shares chooses its organisation, once.
  const linkCode = new URLSearchParams(location.search).get("org");
  const organisation = useSignInOrganisation(linkCode);
  const [orgLinkFailed, setOrgLinkFailed] = useState(false);
  useEffect(() => {
    if (linkCode === null || linkCode.trim() === "") return;
    organisation.choose(linkCode).catch(() => setOrgLinkFailed(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per code in the link
  }, [linkCode]);

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<SignInFormValues>({
    defaultValues: {
      tenant_id: "",
      email: "",
      password: "",
      mfa_code: "",
    },
  });

  // The search comes back with the path, so `/?tour` still opens the demo guide after the
  // sign-in it bounced through. It travels in router state, never in the URL (see RequireAuth).
  const from = (location.state as LocationState | null)?.from;
  const destination =
    from?.pathname === undefined
      ? DEFAULT_SIGNED_IN_ROUTE
      : `${from.pathname}${from.search ?? ""}`;

  if (credentials !== null) {
    return <Navigate to={destination} replace />;
  }

  const onSubmit = handleSubmit(async (values) => {
    setFailure(null);
    try {
      // The form's shape is the generated `TokenRequest`, not a hand-written
      // twin of it (H) doc 27 §4.
      const trimmedCode = values.mfa_code.trim();
      if (organisation.resolving) return; // the link's organisation is still being found
      let tenantId = organisation.tenantId;
      if (tenantId === "") {
        // No organisation known yet: the code typed above names it (backend D202).
        try {
          tenantId = (await organisation.choose(organisationCodeFrom(values.tenant_id))).tenantId;
        } catch {
          setError("tenant_id", {
            type: "server",
            message: "No organisation has that code. Check it with your practice.",
          });
          return;
        }
      }
      const request: TokenRequest = {
        tenant_id: tenantId,
        email: values.email,
        password: values.password,
        // Omitted entirely until there is one. The field is optional on the wire and the
        // *row* decides whether it is required, so sending an empty string would be this
        // form asserting something about the account.
        ...(trimmedCode === "" ? {} : { mfa_code: trimmedCode }),
      };
      await signIn.mutateAsync(request);
    } catch (error) {
      const view = describeProblem(error);

      /**
       * (H) `MFA_REQUIRED` is a prompt, not a failure, and it is the only refusal here that
       * means "ask for one more thing" rather than "something was wrong". Rendering it as an
       * error would tell somebody their password was rejected when it was accepted.
       *
       * Matched on the **code**, never on the message — doc 27 §6 forbids branching on
       * message text, and this is exactly the branch that would be tempting to write that way.
       */
      if (view.code === ErrorCode.MFA_REQUIRED) {
        setMfaRequired(true);
        setFailure(null);
        return;
      }

      // (H) doc 27 §6, the `422` row: field errors are mapped from `errors[]`
      // onto the form, and only what is left over becomes a form-level message.
      const byField = fieldErrorMap(view);
      let anyFieldMatched = false;
      for (const [field, message] of byField) {
        if (
          field === "tenant_id" ||
          field === "email" ||
          field === "password" ||
          field === "mfa_code"
        ) {
          anyFieldMatched = true;
          setError(field, { type: "server", message });
        }
      }
      if (!anyFieldMatched) setFailure(error);
    }
  });

  const pending = isSubmitting || signIn.isPending;

  return (
    <div className="grid min-h-dvh grid-cols-1 bg-canvas lg:grid-cols-[1.05fr_1fr]">
      {/* ── The brand panel ──────────────────────────────────────────────────
          (H) LIGHT, not the brand gradient, and that choice fixed three things
          at once.

          The supplied logo is grey and magenta, drawn for a light ground. On the
          gradient it needed either a white plate (which read as a sticker stuck
          over the panel) or a white knockout (which throws the brand colour
          away). On a light panel the artwork is simply itself.

          doc 40 §12 permits a gradient on a sign-in surface; it does not require
          one. What it forbids is a gradient behind a form, and the form is on
          the right. A soft tint carries the brand here instead, so the strongest
          colour on the screen is the one button a person is meant to press.

          Decorative by definition — everything it says is repeated inside the
          product — so it is hidden from assistive technology rather than read
          out ahead of the form. */}
      <aside
        aria-hidden="true"
        className={[
          "relative hidden flex-col overflow-hidden px-14 py-12 lg:flex",
          // (H) A flat tint, with the gradient demoted to one soft accent below.
          //
          // `bg-gradient-weak` runs rose-400 -> rose-100 left to right,
          // which puts its most saturated stop directly under the headline and
          // the body copy. It cleared the contrast floor (4.75:1, measured) and
          // still read as confectionery rather than as a product. A flat
          // `primary-weak` surface carries the brand without competing with the
          // type, and the accent gives the panel depth without putting colour
          // where the words are.
          "bg-primary-weak",
        ].join(" ")}
      >
        {/* One soft accent, bottom-left, well away from the text. Decorative and
            `aria-hidden`; `blur-3xl` keeps it a wash rather than a shape, and it
            is clipped by the panel's `overflow-hidden`. */}
        <span
          aria-hidden="true"
          className="pointer-events-none absolute -bottom-32 -left-24 h-96 w-96 rounded-full bg-gradient-weak opacity-60 blur-3xl"
        />

        {/* (H) `self-start`. A flex column stretches its children to the full
            width, so this `<img>` was 642px wide and `object-contain` centred
            the artwork inside it — the logo appeared in the middle of the panel
            while everything below it started at the left margin. Nothing was
            wrong with the margin; the image was wrong about its own width. */}
        <Brand className="relative h-9 self-start" decorative />

        {/* The content sits above the midline, not on it. `mt-auto mb-auto`
            centres the block and `pb-28` then biases it upward — the same trick
            the form card uses on the other half, and for the same reason: a
            reader's eye expects this material in the upper-middle of the column.
            (`justify-between` was worse still: it put 194px of dead space
            between the logo and the headline.) */}
        <div className="relative mt-auto mb-auto max-w-lg pb-28">
          {/* (H) `text-3xl` — the scale STOPS there. `text-4xl` is not emitted by
              this theme, so it resolved to nothing and the headline rendered at
              14px, the base size. Same failure shape as an unknown gradient
              class: no error, just the wrong thing. */}
          <h2 className="text-3xl font-bold leading-tight tracking-tight text-default">
            The <span className="text-primary">AI-native</span>
            <br />
            practice management system
          </h2>
          <p className="mt-5 max-w-md text-base leading-relaxed text-secondary">
            Automate the routine, surface what matters, and give your team more
            time for patients.
          </p>

          <ul className="mt-10 flex flex-col gap-5">
            {PROOF_POINTS.map((point) => (
              <li key={point.title} className="flex items-start gap-3.5">
                <span
                  className={[
                    "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg",
                    // (H) `border-default`, not `ring-black/5`. `black` and `white`
                    // are primitives, and doc 40 §3 forbids a component reaching
                    // for one — the lint rule catches it, and it caught this.
                    // There is no ring-colour role to reach for either: the theme
                    // overrides `borderColor` and not `ringColor`, so a border is
                    // the edge this tile should have had.
                    "border border-default bg-default shadow-sm",
                    TILE[point.tone],
                  ].join(" ")}
                >
                  <Icon name={point.icon} size={5} />
                </span>
                <span className="flex flex-col gap-0.5">
                  <span className="text-sm font-semibold text-default">
                    {point.title}
                  </span>
                  <span className="text-sm leading-relaxed text-secondary">
                    {point.body}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </div>

        <p className="relative mt-auto text-xs text-tertiary">
          Sarva Health · Practice management
        </p>
      </aside>

      {/* The form, in a card on the canvas. (H) doc 40 §12: never a gradient
          behind a form — the card is `bg-default`, and the only saturated colour
          on this half of the screen is the submit button. */}
      {/* `pb-28` biases the centring upward. The card was optically centred and
          sat low: the eye reads a sign-in card as belonging in the upper-middle
          of its column, not on the midline. Extra bottom padding lifts it
          without pinning it to the top, so it still centres on a short
          viewport. */}
      <main className="flex flex-col justify-center px-6 pb-28 pt-12 sm:px-12">
        <div className="mx-auto w-full max-w-[27rem] rounded-xl border border-default bg-default p-9 shadow-card sm:p-11">
          {/* The lockup sits in the card as well as on the panel, because below
              `lg` the panel is not rendered at all and this is the only place
              the product names itself. */}
          <div className="flex flex-col items-center text-center">
            <Brand className="mb-6 h-8" decorative />
            <h1 className="text-2xl font-bold tracking-tight text-default">
              Sign in
            </h1>
            <p className="mt-1.5 text-sm text-secondary">
              Continue to the practice management workspace.
            </p>
          </div>

          {embedRequested() ? (
            // Backend D251: inside a partner's frame the way back in is the partner's button,
            // which opens Sarva signed in again — a password here is the fallback, not the path.
            <p
              className="mt-6 rounded-sm border border-info bg-info-weak px-3 py-2 text-sm text-info"
              role="status"
            >
              This session has ended. Close this window and press Bill to insurance again in your
              point of sale.
            </p>
          ) : signedOutReason === "expired" ? (
            <p
              className="mt-6 rounded-sm border border-info bg-info-weak px-3 py-2 text-sm text-info"
              role="status"
            >
              Your session expired. Sign in again to continue where you left
              off.
            </p>
          ) : null}

          <form
            className="mt-8 flex flex-col gap-5"
            onSubmit={onSubmit}
            noValidate
          >
            {organisation.resolving ? (
              <p className="text-sm text-secondary" role="status">
                Finding your organisation…
              </p>
            ) : organisation.tenantId === "" ? (
              <Field
                label="Organisation code"
                required
                hint="The short name in your sign-in link or invitation email, such as resurge-pt. You can paste the whole link. This device remembers it after you sign in."
                {...(errors.tenant_id?.message === undefined
                  ? {}
                  : { error: errors.tenant_id.message })}
              >
                {({ id, "aria-describedby": describedBy, invalid }) => (
                  <Input
                    id={id}
                    identifier
                    autoComplete="organization"
                    spellCheck={false}
                    aria-describedby={describedBy}
                    invalid={invalid}
                    {...register("tenant_id", {
                      required: "Enter your organisation's code.",
                    })}
                  />
                )}
              </Field>
            ) : (
              <p className="flex flex-wrap items-baseline justify-between gap-2 text-sm">
                <span className="text-secondary">
                  Signing in to{" "}
                  <span className="font-medium text-default">
                    {organisation.name ?? "your organisation"}
                  </span>
                </span>
                <button
                  type="button"
                  className="text-sm font-medium text-primary underline"
                  onClick={organisation.clear}
                >
                  Use a different organisation
                </button>
              </p>
            )}
            {orgLinkFailed ? (
              <p role="alert" className="text-sm text-danger">
                The organisation in this link wasn’t found. Enter its code instead.
              </p>
            ) : null}

            <Field
              label="Email"
              required
              {...(errors.email?.message === undefined
                ? {}
                : { error: errors.email.message })}
            >
              {({ id, "aria-describedby": describedBy, invalid }) => (
                <span className="relative block">
                  <Icon
                    name="mail"
                    size={4}
                    className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-tertiary"
                  />
                  <Input
                    id={id}
                    type="email"
                    autoComplete="username"
                    placeholder="name@practice.example"
                    aria-describedby={describedBy}
                    invalid={invalid}
                    className="pl-9"
                    {...register("email", {
                      required: "Enter your email address.",
                    })}
                  />
                </span>
              )}
            </Field>

            <Field
              label="Password"
              required
              {...(errors.password?.message === undefined
                ? {}
                : { error: errors.password.message })}
            >
              {({ id, "aria-describedby": describedBy, invalid }) => (
                <span className="relative block">
                  <Icon
                    name="lock"
                    size={4}
                    className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-tertiary"
                  />
                  <Input
                    id={id}
                    type={revealed ? "text" : "password"}
                    // `current-password`, so a password manager offers the saved
                    // one rather than proposing a new one. Paired with
                    // `autoComplete="username"` on the field above, which is what
                    // tells it the two belong to one credential.
                    autoComplete="current-password"
                    aria-describedby={describedBy}
                    invalid={invalid}
                    className="pl-9 pr-10"
                    {...register("password", {
                      required: "Enter your password.",
                    })}
                  />
                  {/* (H) A real control, not an ornament. A password field is the
                      one input a person cannot proofread, and every wrong guess
                      here costs a round trip and an indistinguishable 401. The
                      button is `tabIndex={-1}` so it does not sit between the
                      field and the submit button in the tab order, and its label
                      changes with its state so a screen reader is told what it
                      will do rather than what it is. */}
                  <button
                    type="button"
                    tabIndex={-1}
                    onClick={() => setRevealed((r) => !r)}
                    aria-label={revealed ? "Hide password" : "Show password"}
                    className="absolute right-2 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-sm text-tertiary hover:text-secondary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-strong"
                  >
                    <Icon name={revealed ? "eye-off" : "eye"} size={4} />
                  </button>
                </span>
              )}
            </Field>
            <Link
              className="-mt-3 self-end text-sm text-secondary underline"
              to={FORGOT_PASSWORD_ROUTE}
            >
              Forgot your password?
            </Link>

            {mfaRequired ? (
              <Field
                label="Authentication code"
                required
                hint="Six digits from your authenticator, or one of your recovery codes."
                {...(errors.mfa_code?.message === undefined
                  ? {}
                  : { error: errors.mfa_code.message })}
              >
                {({ id, "aria-describedby": describedBy, invalid }) => (
                  <Input
                    id={id}
                    // `one-time-code` is what lets a phone offer the code from its SMS or
                    // authenticator rather than making somebody switch apps and retype it.
                    autoComplete="one-time-code"
                    inputMode="text"
                    // (H) Focused on appearance. The password was already accepted, so this
                    // is the only thing left to type — and the alternative is a form that
                    // silently grew a field somebody has to go looking for.
                    autoFocus
                    aria-describedby={describedBy}
                    invalid={invalid}
                    {...register("mfa_code", {
                      required: "Enter the code from your authenticator.",
                    })}
                  />
                )}
              </Field>
            ) : null}

            {failure === null ? null : describeProblem(failure).kind === "unauthenticated" ? (
              /* Backend D199 — every refusal is the same `401` (unknown account, wrong password,
                 wrong code, locked account), so the copy covers all of them without saying
                 which. The server's own "Sign in to continue." reads as nonsense here. */
              <p role="alert" className="text-sm text-danger">
                That didn&apos;t match. Check your email, password and code. After five wrong
                passwords in a row the account is locked for 15 minutes.
              </p>
            ) : (
              <ProblemNotice error={failure} variant="inline" />
            )}

            <Button
              type="submit"
              variant="primary"
              size="lg"
              loading={pending}
              loadingLabel="Signing in…"
              className="mt-1 w-full"
            >
              Sign in
              <Icon name="arrow-right" size={4} />
            </Button>
          </form>
          {coreSignInAvailable ? (
            // Backend D315: the one Sarva sign-in, for people Core manages (Ironwood billers
            // working several practices, and anyone moved onto it). The form above keeps working.
            <Button
              type="button"
              variant="secondary"
              size="lg"
              className="mt-3 w-full"
              onClick={() => window.location.assign(coreSignInUrl())}
            >
              Sign in with Sarva
            </Button>
          ) : null}
        </div>
      </main>
    </div>
  );
}

/**
 * What the organisation field was given, as a code. People paste the sign-in link their
 * practice sent rather than picking the `org=` out of it, so a link is read for its `org`.
 */
export function organisationCodeFrom(entered: string): string {
  const value = entered.trim();
  const match = /[?&]org=([^&#\s]+)/.exec(value);
  return match?.[1] === undefined ? value : decodeURIComponent(match[1]);
}

